import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  Alert,
  Collapse,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import http from "../../../utils/http-common";

type Analysis = {
  summary?: string;
  what_failed?: string;
  what_was_missing?: string;
  possible_solution?: string;
  severity?: string;
  confidence?: string;
  _model?: string;
};

const SEVERITY_COLOR: Record<string, string> = {
  low: "#2e7d32",
  medium: "#d97706",
  high: "#dc2626",
  critical: "#b91c1c",
  unknown: "#6b7280",
};

// Render a value that may contain \n bullet/step lines.
const MultiLine = ({ text }: { text?: string }) => {
  const lines = (text || "").split(/\r?\n/).filter((l) => l.trim());
  if (lines.length <= 1) return <Typography variant="body2">{text}</Typography>;
  return (
    <Stack component="ul" sx={{ m: 0, pl: 2.5 }} spacing={0.4}>
      {lines.map((l, i) => (
        <Typography key={i} component="li" variant="body2">
          {l.replace(/^\s*(\d+\.|[-*•])\s*/, "")}
        </Typography>
      ))}
    </Stack>
  );
};

const Section = ({
  icon,
  title,
  children,
  color,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  color?: string;
}) => (
  <Box>
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
      <Box sx={{ color: color || "text.secondary", display: "flex" }}>{icon}</Box>
      <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.4, color: "text.secondary" }}>
        {title.toUpperCase()}
      </Typography>
    </Stack>
    {children}
  </Box>
);

const AiFailureAnalysis: React.FC<{ caseId: number }> = ({ caseId }) => {
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(true);

  const loadCached = useCallback(async () => {
    try {
      const res = await http.get(`/report/analyze/${caseId}`);
      if (res.data?.analysis) setAnalysis(res.data.analysis);
    } catch {
      /* no cached analysis yet */
    }
  }, [caseId]);

  useEffect(() => {
    loadCached();
  }, [loadCached]);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.post(`/report/analyze/${caseId}`);
      setAnalysis(res.data?.analysis || null);
    } catch (e: any) {
      setError(e?.response?.data?.message || "AI analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const sev = (analysis?.severity || "unknown").toLowerCase();

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 2,
        borderColor: "primary.light",
        background: "linear-gradient(180deg, rgba(233,103,45,0.05) 0%, rgba(233,103,45,0) 60%)",
      }}
    >
      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: analysis || loading ? 1 : 0 }}>
          <AutoAwesomeIcon sx={{ color: "primary.main" }} fontSize="small" />
          <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", flexGrow: 1 }}>
            AI Failure Analysis
          </Typography>
          {analysis && (
            <Chip
              size="small"
              label={sev}
              sx={{ bgcolor: SEVERITY_COLOR[sev] || SEVERITY_COLOR.unknown, color: "#fff", fontWeight: 700, textTransform: "capitalize" }}
            />
          )}
          {analysis && (
            <Chip size="small" variant="outlined" label={`confidence: ${analysis.confidence || "?"}`} />
          )}
          <Button
            size="small"
            variant={analysis ? "text" : "contained"}
            startIcon={loading ? <CircularProgress size={14} /> : analysis ? <RefreshIcon /> : <AutoAwesomeIcon />}
            onClick={run}
            disabled={loading}
          >
            {loading ? "Analyzing…" : analysis ? "Re-analyze" : "Analyze with AI"}
          </Button>
          {analysis && (
            <Button size="small" onClick={() => setOpen((o) => !o)}>
              {open ? "Hide" : "Show"}
            </Button>
          )}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}

        {!analysis && !loading && !error && (
          <Typography variant="body2" color="text.secondary">
            Diagnose this case with AI — what failed, what was missing, and a possible solution.
          </Typography>
        )}

        <Collapse in={open && !!analysis} timeout="auto">
          {analysis && (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {analysis.summary && (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {analysis.summary}
                </Typography>
              )}
              <Divider />
              {analysis.what_failed && (
                <Section icon={<ErrorOutlineIcon fontSize="small" />} title="What failed" color="#dc2626">
                  <MultiLine text={analysis.what_failed} />
                </Section>
              )}
              {analysis.what_was_missing && (
                <Section icon={<HelpOutlineIcon fontSize="small" />} title="What was missing" color="#d97706">
                  <MultiLine text={analysis.what_was_missing} />
                </Section>
              )}
              {analysis.possible_solution && (
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    bgcolor: "rgba(46,125,50,0.08)",
                    border: "1px solid",
                    borderColor: "rgba(46,125,50,0.35)",
                  }}
                >
                  <Section icon={<LightbulbOutlinedIcon fontSize="small" />} title="Possible solution" color="#2e7d32">
                    <MultiLine text={analysis.possible_solution} />
                  </Section>
                </Box>
              )}
              {analysis._model && (
                <Typography variant="caption" color="text.secondary">
                  Generated by {analysis._model}
                </Typography>
              )}
            </Stack>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default AiFailureAnalysis;
